/**
 * Modal for X (Twitter) follow quest verification flow.
 * Descendant of ModalFrame — dark themed, unclosable (CTA only).
 *   - 'error': First verify click — scare user, CTA opens X + closes (red)
 *   - 'pending': Second verify click — submitted for moderation (orange)
 *   - 'info': Tapping moderation button — moderation status (orange, NO progress bar)
 *
 * CTA button has a 5-second progress bar for error/pending variants.
 * Info variant shows an instant OK button (no delay).
 */
import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import ModalFrame from '../primitives/ModalFrame';
import { cn } from '../cn';


type XQuestVerificationVariant = 'error' | 'pending' | 'info';

interface XQuestVerificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  variant: XQuestVerificationVariant;
  xUrl: string;
}

const DELAY_MS = 5000;
const TICK_MS = 50;

const ERROR_ACCENT = 'var(--color-error-bright)';
const ORANGE_ACCENT = 'var(--color-xquest-verification-modal-accent)';

function getScheme(variant: XQuestVerificationVariant) {
  if (variant === 'error') {
    return {
      accent: ERROR_ACCENT,
      topBar: `linear-gradient(90deg, ${ERROR_ACCENT} 0%, var(--color-accent) 100%)`,
      iconBg: 'var(--color-danger-alpha-15)',
      iconGlow: '0 0 24px var(--color-danger-alpha-25)',
      btnBg: `linear-gradient(135deg, ${ERROR_ACCENT} 0%, var(--color-xquest-verification-modal-accent-2) 100%)`,
      btnShadow: `0 4px 16px var(--color-danger-alpha-35)`,
      btnFillBg: 'var(--color-danger-alpha-25)',
      linkBg: 'var(--color-danger-alpha-8)',
      linkBorder: 'var(--color-danger-alpha-20)',
    };
  }
  // pending + info → orange
  return {
    accent: ORANGE_ACCENT,
    topBar: `linear-gradient(90deg, ${ORANGE_ACCENT} 0%, var(--color-duel-playing-view-accent) 100%)`,
    iconBg: 'var(--color-warning-alpha-15)',
    iconGlow: '0 0 24px var(--color-warning-alpha-25)',
    btnBg: `linear-gradient(135deg, ${ORANGE_ACCENT} 0%, var(--color-xquest-verification-modal-accent-3) 100%)`,
    btnShadow: `0 4px 16px var(--color-warning-alpha-35)`,
    btnFillBg: 'var(--color-warning-alpha-25)',
    linkBg: 'var(--color-warning-alpha-8)',
    linkBorder: 'var(--color-warning-alpha-20)',
  };
}

export default function XQuestVerificationModal({
  isOpen,
  onClose,
  variant,
  xUrl,
}: XQuestVerificationModalProps) {
  const { t } = useTranslation();
  // Info variant has no progress bar — button is instantly ready
  const hasProgressBar = variant !== 'info';
  const [progress, setProgress] = useState(() => (isOpen && !hasProgressBar ? 100 : 0));
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [wasOpen, setWasOpen] = useState(isOpen);

  // (b) Reset progress when modal opens — during render
  if (isOpen !== wasOpen) {
    setWasOpen(isOpen);
    if (isOpen) {
      setProgress(hasProgressBar ? 0 : 100);
    }
  }

  // (c) Progress timer — setState only in interval callback
  useEffect(() => {
    if (!isOpen || !hasProgressBar) return;
    const start = Date.now();
    intervalRef.current = setInterval(() => {
      const elapsed = Date.now() - start;
      const pct = Math.min(100, (elapsed / DELAY_MS) * 100);
      setProgress(pct);
      if (pct >= 100 && intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }, TICK_MS);
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isOpen, hasProgressBar]);

  const ready = progress >= 100;
  const config = getVariantConfig(variant, t);
  const scheme = getScheme(variant);

  const handleCta = () => {
    if (!ready) return;
    if (variant === 'error') {
      window.open(xUrl, '_blank');
    }
    onClose();
  };

  return (
    <ModalFrame
      isOpen={isOpen}
      onClose={() => {}}
      variant="dialog"
      className="max-w-[380px] rounded-xl bg-surface-raised p-0"
      showBackButton={false}
      hideHeader
      closeOnBackdropClick={false}
    >
      {/* Accent top bar */}
      <div
        className="h-1 rounded-t-xl"
        style={{ background: scheme.topBar }}
      />

      <div className="flex flex-col items-center gap-xl rounded-b-xl bg-[linear-gradient(180deg,var(--color-surface-raised)_0%,var(--color-xquest-verification-modal-surface)_100%)] px-3xl pt-[28px] pb-3xl text-center">
        {/* Icon */}
        <div
          className="flex h-16 w-16 items-center justify-center rounded-full text-[32px]"
          style={{
            background: scheme.iconBg,
            boxShadow: scheme.iconGlow,
          }}
        >
          {config.icon}
        </div>

        {/* Title */}
        <h2 className="m-0 [font-family:'Rubik_One',sans-serif] text-[20px] font-normal text-white">
          {config.title}
        </h2>

        {/* Message */}
        <p className="m-0 max-w-[300px] [font-family:Lato,sans-serif] text-[14px] leading-[20px] font-normal text-white-alpha-60">
          {config.message}
        </p>

        {/* X link chip (not in error variant — error CTA opens X directly) */}
        {variant !== 'error' && variant !== 'info' && (
          <a
            href={xUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="inline-flex items-center gap-md rounded-md px-xl py-md no-underline"
            style={{
              background: scheme.linkBg,
              border: `1px solid ${scheme.linkBorder}`,
            }}
          >
            <img
              src="/ui/daily-quests/x-logo.png"
              alt="X"
              className="h-[18px] w-[18px] object-contain"
            />
            <span
              className="break-all [font-family:Lato,sans-serif] text-[13px] font-bold"
              style={{ color: scheme.accent }}
            >
              {xUrl}
            </span>
          </a>
        )}

        {/* CTA button — with progress bar for error/pending, instant for info */}
        <button
          onClick={handleCta}
          disabled={!ready}
          className={cn(
            'relative mt-xs flex w-full max-w-[260px] items-center justify-center gap-md overflow-hidden rounded-[32px] px-3xl py-lg [font-family:"Rubik_One",sans-serif] text-[13px] font-normal transition-[background,color,box-shadow,border] duration-300',
            ready ? 'cursor-pointer border-none text-white' : 'cursor-not-allowed text-white-alpha-35',
          )}
          style={{
            background: ready ? scheme.btnBg : 'var(--color-white-alpha-6)',
            border: ready ? 'none' : `1px solid ${scheme.accent}40`,
            boxShadow: ready ? scheme.btnShadow : 'none',
          }}
        >
          {/* Fill bar behind text (only for error/pending) */}
          {hasProgressBar && !ready && (
            <div
              className="absolute top-0 bottom-0 left-0 rounded-[32px]"
              style={{
                width: `${progress}%`,
                background: scheme.btnFillBg,
                transition: `width ${TICK_MS}ms linear`,
              }}
            />
          )}
          <span className="relative z-[1] flex items-center gap-md">
            {ready ? (
              variant === 'error' ? (
                <>
                  <img src="/ui/daily-quests/x-logo.png" alt="X" className="h-4 w-4 object-contain" />
                  {t('Try again')}
                </>
              ) : 'OK'
            ) : (
              `${Math.ceil((DELAY_MS - (progress / 100) * DELAY_MS) / 1000)}s`
            )}
          </span>
        </button>
      </div>
    </ModalFrame>
  );
}

function getVariantConfig(variant: XQuestVerificationVariant, t: (key: string) => string) {
  switch (variant) {
    case 'error':
      return {
        icon: '⚠️',
        title: t('Not verified'),
        message: t('It looks like you haven\'t followed our X account yet. Please make sure you are subscribed and try again.'),
      };
    case 'pending':
      return {
        icon: '⏳',
        title: t('Submitted'),
        message: t('Your follow is being verified. If confirmed, you\'ll receive the reward. This may take up to 24 hours.'),
      };
    case 'info':
      return {
        icon: '⏳',
        title: t('Verification in progress'),
        message: t('Please wait. A moderator is verifying your subscription. You will receive the reward once confirmed.'),
      };
  }
}
